import type { CheckItem } from '../types';
import { textareaOffsetAtPoint } from '../utils/textareaSelectionRect';
import { attachLongPress } from './longPress';
import {
  sentenceAtItemTouch,
  sentenceAtOffset,
  textOffsetAtPoint,
  wordAtItemTouch,
  wordAtOffset,
} from './sentenceAtPoint';
import { translateWithSentenceFollowUp } from './selectionTranslate';
import { freezeAnchorRect } from './translateAnchor';
import { hideTranslateBubble } from './translateBubble';

export function isCoarsePointerDevice(): boolean {
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

function anchorRect(clientX: number, clientY: number): DOMRect {
  return new DOMRect(clientX - 2, clientY - 10, 4, 20);
}

function normalizePhrase(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

function translateAtPoint(primary: string, sentence: string, clientX: number, clientY: number): void {
  const word = normalizePhrase(primary);
  const fullSentence = normalizePhrase(sentence);
  const wordInSentence =
    word.length >= 2 && fullSentence.length >= 2 && word !== fullSentence;
  const toTranslate = wordInSentence ? fullSentence : word.length >= 2 ? word : fullSentence;
  if (toTranslate.length < 2) return;

  const rect = freezeAnchorRect(anchorRect(clientX, clientY));
  hideTranslateBubble();

  translateWithSentenceFollowUp(toTranslate, rect, wordInSentence ? word : undefined);
}

function onLongPressItemText(
  textEl: HTMLElement,
  item: CheckItem,
  clientX: number,
  clientY: number
): void {
  const hit =
    document.elementFromPoint(clientX, clientY)?.closest('.item-body, .item-text') ?? textEl;
  const hitEl = hit instanceof HTMLElement ? hit : textEl;

  const previewPlain = hitEl.textContent ?? '';
  const offset = textOffsetAtPoint(hitEl, clientX, clientY);
  if (offset === null) return;

  const sentence = sentenceAtItemTouch(item.text, previewPlain, offset);
  const word = wordAtItemTouch(item.text, previewPlain, offset);
  translateAtPoint(word, sentence, clientX, clientY);
}

function onLongPressTextarea(
  textarea: HTMLTextAreaElement,
  clientX: number,
  clientY: number
): void {
  const offset = textareaOffsetAtPoint(textarea, clientX, clientY);
  if (offset === null) return;
  const sentence = sentenceAtOffset(textarea.value, offset);
  const word = wordAtOffset(textarea.value, offset);
  translateAtPoint(word, sentence, clientX, clientY);
}

/** Long-press the item editor textarea (phone / tablet). */
export function attachTextareaLongPressTranslate(textarea: HTMLTextAreaElement): () => void {
  if (!isCoarsePointerDevice()) return () => {};

  return attachLongPress(textarea, ({ clientX, clientY }) => {
    onLongPressTextarea(textarea, clientX, clientY);
  });
}

/**
 * Touch / phone: long-press item text → translate the surrounding sentence.
 */
export function wireItemLongPressTranslate(
  root: ParentNode,
  getItem: (sectionIndex: number, itemIndex: number) => CheckItem | undefined
): void {
  if (!isCoarsePointerDevice()) return;

  root.querySelectorAll<HTMLElement>('.item-text').forEach(textEl => {
    const row = textEl.closest<HTMLElement>('li.item-row');
    if (!row) return;
    const si = parseInt(row.dataset['section'] ?? '', 10);
    const ii = parseInt(row.dataset['item'] ?? '', 10);
    if (Number.isNaN(si) || Number.isNaN(ii)) return;

    attachLongPress(textEl, ({ clientX, clientY }) => {
      const item = getItem(si, ii);
      if (!item) return;
      onLongPressItemText(textEl, item, clientX, clientY);
    });
  });
}
